import { useMemo, useEffect } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { QueryVariables } from '../../../types';
import { FiltersFormData } from '../../../hooks/use-filters-form';
import type { CategorizedLoadingStates } from '../../../hooks/use-loading-states';

export interface Badge {
  id: string;
  names: string[];
}

interface IBadgeProps {
  badges: Record<string, Badge>;
  onDismiss: (id: string) => (event: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  onDismissItem: (id: string, itemName: string) => void;
  onClick: () => void;
  onClearAll: () => void;
  loadingStates: CategorizedLoadingStates;
}

interface UseSearchBadgesReturn {
  badgesProps: IBadgeProps;
}

interface useSearchBadgesParams {
  queryVariables: QueryVariables;
  form: UseFormReturn<FiltersFormData>;
  handleClearAll: () => void;
  handleSearch: () => void;
  setIsFiltersOpen: (open: boolean) => void;
  updateUrlParams: (params: Partial<QueryVariables>) => void;
  loadingStates: CategorizedLoadingStates;
  generateFilter?: (prompt: string, isUserEntered?: boolean) => void;
  buildInitialPrompt?: () => string;
}

// Helper function to safely convert array values to strings
const processArrayValue = <T>(value: T[]): string[] => {
  return value
    .filter((v): v is NonNullable<T> => v !== null && v !== undefined)
    .map(String);
};

const useSearchBadges = ({ queryVariables, form, handleClearAll, handleSearch, setIsFiltersOpen, updateUrlParams, loadingStates, generateFilter, buildInitialPrompt }: useSearchBadgesParams): UseSearchBadgesReturn => {
  useEffect(() => {
    if (!queryVariables) return;

    let isMounted = true;

    // Update form values to match queryVariables
    Object.entries(queryVariables).forEach(([key, value]) => {
      if (value !== null && value !== undefined && isMounted) {
        form.setValue(key as keyof FiltersFormData, value);
      }
    });

    return () => {
      isMounted = false;
    };
  }, [queryVariables, form]);

  const badges = useMemo(() => {
    if (!queryVariables) return {};

    return Object.entries(queryVariables)
      .filter(([, value]) => {
        if (value === null || value === undefined) return false;
        if (Array.isArray(value)) return value.length > 0 && value.some((v) => v !== null && v !== undefined);
        return value !== '';
      })
      .reduce((acc, [key, value]) => {
        acc[key] = {
          id: key,
          names: Array.isArray(value)
            ? processArrayValue(value)
            : [String(value)],
        };
        return acc;
      }, {} as Record<string, { id: string; names: string[] }>);
  }, [queryVariables]);

  const handleDismiss = (id: string) => async (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
    event.stopPropagation();

    const newQueryVariables = { ...queryVariables };

    // Handle different field types appropriately
    if (id === 'query') {
      // For query: simply clear it
      newQueryVariables[id] = '';
      form.setValue(id as keyof FiltersFormData, '');
    } else if (id === 'prompt') {
      // For prompt: clear it from URL/form and return to system-generated default behavior
      delete newQueryVariables[id]; // Remove from URL entirely
      form.setValue(id as keyof FiltersFormData, '');

      // Trigger default system prompt generation without adding to URL
      if (generateFilter && buildInitialPrompt) {
        generateFilter(buildInitialPrompt(), false); // Mark as system-generated, not user-entered
      }
    } else if (id === 'similarToCreators') {
      newQueryVariables[id] = null; // Set similarToCreators to null
      form.setValue(id as keyof FiltersFormData, null);
    } else {
      newQueryVariables[id] = []; // Set other fields to empty array
      form.setValue(id as keyof FiltersFormData, []);
    }

    await updateUrlParams(newQueryVariables);
    handleSearch();
  };

  const handleDismissItem = async (id: string, itemName: string) => {
    const newQueryVariables = { ...queryVariables };

    if (id === 'similarToCreators' && Array.isArray(queryVariables.similarToCreators)) {
      // Remove the specific creator from the array
      const updatedCreators = queryVariables.similarToCreators.filter((creator) => creator !== itemName);
      newQueryVariables.similarToCreators = updatedCreators.length > 0 ? updatedCreators : null;
      form.setValue('similarToCreators', updatedCreators.length > 0 ? updatedCreators : null);
    }

    await updateUrlParams(newQueryVariables);
    handleSearch();
  };

  const onClearAll = () => {
    // Reset form fields to their default values
    form.setValue('query', ''); // Clear the query field
    form.setValue('prompt', ''); // Clear the prompt field
    form.setValue('minFollowers', null);
    form.setValue('maxFollowers', null);
    form.setValue('minEngagements', null);
    form.setValue('maxEngagements', null);
    form.setValue('similarToCreators', null);

    // For clear all, don't trigger default prompt behavior - just clear everything
    handleClearAll();
  };

  const handleOpenFilters = () => {
    setIsFiltersOpen(true);
  };

  return {
    badgesProps: {
      badges,
      onClick: handleOpenFilters,
      onDismiss: handleDismiss,
      onDismissItem: handleDismissItem,
      onClearAll,
      loadingStates,
    },
  };
};

export default useSearchBadges;
